const MANIFESTS = {
  admin: "/pwa/manifests/admin-manifest.json?v=20260716-2",
  restaurant: "/pwa/manifests/restaurant-manifest.json?v=20260716-1",
};

const APP_INSTALL_COPY = {
  admin: {
    name: "WilReji Admin",
    message: "Get WilReji Admin app",
  },
  restaurant: {
    name: "WilReji Restaurant",
    message: "Get WilReji Restaurant app",
  },
};

const INSTALL_DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
const INSTALL_BANNER_ID = "wilreji-pwa-install-banner";
const INSTALL_BANNER_STYLE_ID = "wilreji-pwa-install-style";
const INSTALL_ICON_PATH = "/pwa/icons/logo192.png";
// Published on :root so anything else pinned to the bottom edge — the chatbot
// launcher, its full-screen mobile panel — can lift itself clear of the banner
// instead of being covered by it. Always defined: 0px while the banner is hidden.
const INSTALL_BANNER_HEIGHT_VAR = "--wilreji-pwa-banner-height";
const SERVICE_WORKER_PATH = "/sw.js?v=20260715-3";
const ROUTE_SYNC_HISTORY_METHODS = ["pushState", "replaceState"];

const installPromptState = {
  app: null,
  event: null,
};

const pwaRuntimeState = {
  isConfigured: false,
};

const getAppFromPath = (pathname) => {
  const app = pathname.split("/").filter(Boolean)[0];
  return MANIFESTS[app] ? app : null;
};

const getManifestLink = () => document.getElementById("pwa-manifest");

const syncManifest = (pathname) => {
  const manifestLink = getManifestLink();
  if (!manifestLink) {
    return;
  }

  const app = getAppFromPath(pathname);
  if (MANIFESTS[app]) {
    manifestLink.setAttribute("href", MANIFESTS[app]);
    return;
  }

  manifestLink.removeAttribute("href");
};

const isStandaloneDisplay = () => (
  window.matchMedia?.("(display-mode: standalone)")?.matches ||
  window.navigator.standalone === true
);

const getDismissKey = (app) => `wilreji:${app}:pwa-install-dismissed-at`;

const getInstalledKey = (app) => `wilreji:${app}:pwa-installed`;

const readLocalStorage = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeLocalStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures. The current page view still works without persistence.
  }
};

const isPromptDismissed = (app) => {
  const dismissedAt = Number(readLocalStorage(getDismissKey(app)));
  return Boolean(dismissedAt && Date.now() - dismissedAt < INSTALL_DISMISS_MS);
};

const dismissPrompt = (app) => {
  writeLocalStorage(getDismissKey(app), String(Date.now()));
};

const isAppInstalled = (app) => readLocalStorage(getInstalledKey(app)) === "1";

const markAppInstalled = (app) => {
  writeLocalStorage(getInstalledKey(app), "1");
};

const resetInstallPromptState = () => {
  installPromptState.event = null;
  installPromptState.app = null;
};

const ensureInstallBannerStyles = () => {
  if (document.getElementById(INSTALL_BANNER_STYLE_ID)) {
    return;
  }

  const style = document.createElement("style");
  style.id = INSTALL_BANNER_STYLE_ID;
  style.textContent = `
    .wilreji-pwa-install-banner {
      position: fixed;
      inset: auto 0 0 0;
      z-index: 2147483000;
      box-sizing: border-box;
      min-height: 76px;
      padding: 18px 20px;
      display: none;
      align-items: center;
      gap: 16px;
      color: #f7f7f7;
      background: #202124;
      box-shadow: 0 -12px 28px rgba(0, 0, 0, 0.22);
      font-family: inherit;
    }

    .wilreji-pwa-install-banner.is-visible {
      display: flex;
    }

    .wilreji-pwa-install-icon {
      width: 48px;
      height: 48px;
      flex: 0 0 48px;
      align-self: flex-start;
      border-radius: 12px;
      background: #ffffff;
      object-fit: contain;
      padding: 6px;
    }

    .wilreji-pwa-install-message {
      flex: 1 1 auto;
      min-width: 0;
      margin: 0;
      color: rgba(255, 255, 255, 0.85);
      font-size: 14px;
      font-weight: 500;
      line-height: 1.35;
      text-align: left;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
    }

    .wilreji-pwa-install-actions {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      gap: 18px;
    }

    .wilreji-pwa-install-primary {
      flex: 0 0 auto;
      min-width: 96px;
      min-height: 40px;
      border: 0;
      border-radius: 999px;
      padding: 9px 22px;
      color: #ffffff;
      background: #ff7a16;
      font-size: 15px;
      font-weight: 700;
      white-space: nowrap;
      cursor: pointer;
    }

    .wilreji-pwa-install-secondary {
      flex: 0 0 auto;
      border: 0;
      color: rgba(255, 255, 255, 0.75);
      background: transparent;
      padding: 9px 6px;
      font-size: 15px;
      font-weight: 600;
      white-space: nowrap;
      cursor: pointer;
    }

    /* Tablet */
    @media (min-width: 768px) and (max-width: 1024px) {
      .wilreji-pwa-install-banner {
        min-height: 68px;
        padding: 14px 16px;
        gap: 14px;
      }

      .wilreji-pwa-install-icon {
        width: 40px;
        height: 40px;
        flex-basis: 40px;
        border-radius: 9px;
        padding: 4px;
      }

      .wilreji-pwa-install-message {
        font-size: 13px;
        line-height: 1.3;
      }

      .wilreji-pwa-install-actions {
        gap: 14px;
      }

      .wilreji-pwa-install-primary {
        min-width: 88px;
        min-height: 36px;
        padding: 8px 18px;
        font-size: 13px;
      }

      .wilreji-pwa-install-secondary {
        padding: 8px 4px;
        font-size: 13px;
      }
    }

    /* Phone */
    @media (max-width: 767px) {
      .wilreji-pwa-install-banner {
        min-height: 60px;
        padding: 12px 12px;
        gap: 10px;
      }

      .wilreji-pwa-install-icon {
        width: 32px;
        height: 32px;
        flex-basis: 32px;
        border-radius: 7px;
        padding: 3px;
      }

      .wilreji-pwa-install-message {
        font-size: 13px;
        line-height: 1.3;
      }

      .wilreji-pwa-install-actions {
        gap: 10px;
      }

      .wilreji-pwa-install-primary {
        min-width: 74px;
        min-height: 32px;
        padding: 6px 14px;
        font-size: 12px;
      }

      .wilreji-pwa-install-secondary {
        padding: 6px 3px;
        font-size: 12px;
      }
    }
  `;
  document.head.appendChild(style);
};

const setBannerHeightVar = (height) => {
  document.documentElement.style.setProperty(INSTALL_BANNER_HEIGHT_VAR, `${height}px`);
};

const syncBannerHeightVar = () => {
  const banner = document.getElementById(INSTALL_BANNER_ID);
  const isVisible = banner?.classList.contains("is-visible");
  setBannerHeightVar(isVisible ? banner.offsetHeight : 0);
};

const observeBannerHeight = (banner) => {
  // The banner grows and shrinks across breakpoints and when the copy wraps, so
  // the offset has to track it rather than being measured once.
  if (typeof ResizeObserver === "function") {
    new ResizeObserver(syncBannerHeightVar).observe(banner);
    return;
  }

  window.addEventListener("resize", syncBannerHeightVar);
};

const hideInstallBanner = () => {
  document.getElementById(INSTALL_BANNER_ID)?.classList.remove("is-visible");
  setBannerHeightVar(0);
};

const handleInstallClick = async () => {
  if (!installPromptState.event) {
    return;
  }

  const promptEvent = installPromptState.event;
  resetInstallPromptState();
  hideInstallBanner();

  promptEvent.prompt();
  try {
    const { outcome } = await promptEvent.userChoice;
    if (outcome === "accepted") {
      const app = getAppFromPath(window.location.pathname);
      if (app) {
        markAppInstalled(app);
      }
    }
  } catch {
    // Browser prompt result is informational only.
  }
};

const handleDismissClick = () => {
  const app = getAppFromPath(window.location.pathname);
  if (app) {
    dismissPrompt(app);
  }
  hideInstallBanner();
};

const createInstallBanner = () => {
  const banner = document.createElement("div");
  banner.id = INSTALL_BANNER_ID;
  banner.className = "wilreji-pwa-install-banner";
  banner.setAttribute("role", "region");
  banner.setAttribute("aria-label", "Install app");
  banner.innerHTML = `
    <img class="wilreji-pwa-install-icon" src="${INSTALL_ICON_PATH}" alt="" />
    <p class="wilreji-pwa-install-message"></p>
    <div class="wilreji-pwa-install-actions">
      <button type="button" class="wilreji-pwa-install-secondary">Not now</button>
      <button type="button" class="wilreji-pwa-install-primary">Install</button>
    </div>
  `;

  banner.querySelector(".wilreji-pwa-install-primary")?.addEventListener("click", handleInstallClick);
  banner.querySelector(".wilreji-pwa-install-secondary")?.addEventListener("click", handleDismissClick);
  document.body.appendChild(banner);
  observeBannerHeight(banner);
  return banner;
};

const getInstallBanner = () => {
  ensureInstallBannerStyles();
  return document.getElementById(INSTALL_BANNER_ID) || createInstallBanner();
};

const shouldRenderInstallBanner = (app) => Boolean(
  app &&
  app === installPromptState.app &&
  installPromptState.event &&
  !isStandaloneDisplay() &&
  !isPromptDismissed(app) &&
  !isAppInstalled(app)
);

const renderInstallBanner = () => {
  const app = getAppFromPath(window.location.pathname);

  if (!shouldRenderInstallBanner(app)) {
    hideInstallBanner();
    return;
  }

  const copy = APP_INSTALL_COPY[app];
  const banner = getInstallBanner();
  const message = banner.querySelector(".wilreji-pwa-install-message");
  const icon = banner.querySelector(".wilreji-pwa-install-icon");

  if (message) {
    message.textContent = copy.message;
  }

  if (icon) {
    icon.setAttribute("alt", `${copy.name} icon`);
  }

  banner.classList.add("is-visible");
  syncBannerHeightVar();
};

const syncPwaRouteState = () => {
  syncManifest(window.location.pathname);
  renderInstallBanner();
};

const handleBeforeInstallPrompt = (event) => {
  const app = getAppFromPath(window.location.pathname);
  if (!app || isPromptDismissed(app) || isAppInstalled(app) || isStandaloneDisplay()) {
    return;
  }

  event.preventDefault();
  installPromptState.event = event;
  installPromptState.app = app;
  renderInstallBanner();
};

const handleAppInstalled = () => {
  const app = getAppFromPath(window.location.pathname);
  if (app) {
    markAppInstalled(app);
  }
  resetInstallPromptState();
  hideInstallBanner();
};

const wrapHistoryMethod = (method) => {
  const original = window.history[method];
  window.history[method] = (...args) => {
    const result = original.apply(window.history, args);
    syncPwaRouteState();
    return result;
  };
};

const registerServiceWorker = () => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  window.addEventListener("load", () => {
    navigator.serviceWorker.register(SERVICE_WORKER_PATH).catch((error) => {
      console.error("PWA service worker registration failed:", error);
    });
  });
};

export const configurePwa = () => {
  syncManifest(window.location.pathname);

  if (pwaRuntimeState.isConfigured) {
    renderInstallBanner();
    return;
  }

  pwaRuntimeState.isConfigured = true;
  window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  window.addEventListener("appinstalled", handleAppInstalled);
  window.addEventListener("popstate", syncPwaRouteState);
  ROUTE_SYNC_HISTORY_METHODS.forEach(wrapHistoryMethod);
  registerServiceWorker();
};
